

import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from "@/shared/ui/button";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/shared/ui/dialog";
import { Input } from "@/shared/ui/input";
import {
    Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage,
} from "@/shared/ui/form";
import { Switch } from "@/shared/ui/switch";
import { CompanyVehicle, CompanyVehicleInsert, CompanyVehicleUpdate } from '@/types';


// バリデーションスキーマ
const formSchema = z.object({
    name: z.string().min(1, { message: "車両名は必須です。" }).max(50, { message: "車両名は50文字以内で入力してください。" }),
    daily_cost: z.number().min(0, { message: "日額料金は0以上で入力してください。" }),
    is_active: z.boolean(),
});

type CompanyVehicleFormData = z.infer<typeof formSchema>;

// 社用車情報のフォームダイアログのprops
interface CompanyVehicleFormDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: CompanyVehicleInsert | CompanyVehicleUpdate) => Promise<boolean>;
    defaultValues?: CompanyVehicle | null;
    isUpdate: boolean;
}

/**
 * 社用車情報のフォームダイアログ
 * @param isOpen ダイアログが開いているかどうか
 * @param onClose ダイアログを閉じる
 * @param onSubmit フォームの送信処理
 * @param defaultValues デフォルト値
 * @param isUpdate 更新かどうか
 * @returns 社用車情報のフォームダイアログ
 */
export default function CompanyVehicleFormDialog({ isOpen, onClose, onSubmit, defaultValues, isUpdate }: CompanyVehicleFormDialogProps) {
    const form = useForm<CompanyVehicleFormData>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            name: defaultValues?.name ?? '',
            daily_cost: defaultValues?.daily_cost ?? 0,
            is_active: defaultValues?.is_active ?? true,
        },
    });

    React.useEffect(() => {
        form.reset({
            name: defaultValues?.name ?? '',
            daily_cost: defaultValues?.daily_cost ?? 0,
            is_active: defaultValues?.is_active ?? true,
        });
    }, [defaultValues, form]);

    const handleFormSubmit = async (values: CompanyVehicleFormData) => {
        // 型定義に合わせてデータを変換
        const dataToSubmit = isUpdate && defaultValues?.id
            ? {
                ...values,
                id: defaultValues.id,
                updated_by: 'current-user' // TODO: 実際のユーザーIDを設定
            }
            : {
                ...values,
                tenant_id: 'default-tenant', // TODO: 実際のテナントIDを設定
                license_plate: '', // ナンバープレートは空文字で設定
                created_by: 'current-user', // TODO: 実際のユーザーIDを設定
                updated_by: 'current-user' // TODO: 実際のユーザーIDを設定
            };

        const success = await onSubmit(dataToSubmit);
        if (success) {
            form.reset(); // 成功したらフォームをリセット
            onClose();
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{isUpdate ? '社用車情報の編集' : '新規社用車の追加'}</DialogTitle>
                    <DialogDescription>
                        {isUpdate ? '社用車の情報を更新します。' : '新しい社用車の情報を入力してください。'}
                    </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4 py-4">
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>車両名</FormLabel>
                                    <FormControl>
                                        <Input placeholder="ハイエース1号" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="daily_cost"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>日額料金</FormLabel>
                                    <FormControl>
                                        <Input
                                            type="number"
                                            placeholder="8000"
                                            {...field}
                                            onChange={(e) => field.onChange(Number(e.target.value))}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="is_active"
                            render={({ field }) => (
                                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                                    <div className="space-y-0.5">
                                        <FormLabel>有効状態</FormLabel>
                                        <FormDescription>
                                            この車両を現在使用可能にするか設定します。
                                        </FormDescription>
                                    </div>
                                    <FormControl>
                                        <Switch
                                            checked={field.value}
                                            onCheckedChange={field.onChange}
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={onClose}>キャンセル</Button>
                            <Button type="submit" disabled={form.formState.isSubmitting}>
                                {form.formState.isSubmitting ? '保存中...' : (isUpdate ? '更新' : '追加')}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
} 